import React, { useEffect, useMemo, useState } from "react";
import api, { formatPHP, formatDate, formatDateTime, formatErrorDetail } from "@/lib/api";
import { PageHeader, Surface, TableWrap, Empty } from "@/components/app/Primitives";
import { Landmark, Download, AlertTriangle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const FINALIZED = ["ready_to_lock", "locked", "released", "paid", "closed"];
const FMT_LABEL = {
    generic: "Generic CSV", bdo: "BDO", bpi: "BPI", unionbank: "UnionBank", metrobank: "Metrobank",
    securitybank: "Security Bank", landbank: "Landbank", chinabank: "Chinabank", rcbc: "RCBC", pnb: "PNB",
};

export default function Disbursement() {
    const [runs, setRuns] = useState([]);
    const [selected, setSelected] = useState(null);
    const [preview, setPreview] = useState(null);
    const [history, setHistory] = useState([]);
    const [formats, setFormats] = useState(["generic"]);
    const [formatItems, setFormatItems] = useState([]);
    const [fmt, setFmt] = useState("generic");
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        api.get("/payroll/runs").then((r) => {
            const list = (Array.isArray(r.data) ? r.data : r.data.items || []).filter((x) => FINALIZED.includes(x.status));
            setRuns(list);
            if (list.length) setSelected(list[0].id);
        }).catch(() => {});
        api.get("/payroll/finance/bank-formats").then((r) => { setFormats(r.data.formats || []); setFormatItems(r.data.items || []); }).catch(() => {});
    }, []);

    const load = (runId) => {
        if (!runId) return;
        setLoading(true); setPreview(null);
        Promise.all([
            api.get(`/payroll/finance/runs/${runId}/disbursement/preview`),
            api.get(`/payroll/finance/runs/${runId}/disbursement/history`),
        ]).then(([p, h]) => { setPreview(p.data); setHistory(h.data.items || []); })
          .catch((e) => toast.error(formatErrorDetail(e.response?.data?.detail) || "Failed to load"))
          .finally(() => setLoading(false));
    };
    useEffect(() => { load(selected); }, [selected]);

    const download = () => {
        if (!selected) return;
        window.open(`/api/payroll/finance/runs/${selected}/bank-export.csv?bank=${fmt}`, "_blank");
        toast.success(`Generating ${FMT_LABEL[fmt] || fmt} file…`);
        setTimeout(() => load(selected), 1200); // refresh history
    };

    const selectedRun = useMemo(() => runs.find((r) => r.id === selected), [runs, selected]);

    return (
        <div>
            <PageHeader title="Bank Disbursement" subtitle="Review the payout register for a finalized payroll run and generate the bank upload file. Only approved / locked runs can be disbursed." />
            <div className="grid gap-4 lg:grid-cols-[300px_1fr]">
                <Surface className="p-3">
                    <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground px-1 pb-2">Finalized runs</div>
                    {!runs.length && <Empty title="No finalized runs" subtitle="Lock or approve a payroll run to disburse it." />}
                    <div className="space-y-1">
                        {runs.map((r) => (
                            <button key={r.id} onClick={() => setSelected(r.id)}
                                className={`w-full rounded-lg border p-3 text-left hover:bg-muted ${selected === r.id ? "bg-muted border-primary/40" : ""}`}
                                data-testid={`disb-run-${r.id}`}>
                                <div className="flex items-center justify-between gap-2">
                                    <div className="font-medium text-sm">{r.period_start} → {r.period_end}</div>
                                    <span className="badge badge-approved capitalize">{String(r.status).replace(/_/g, " ")}</span>
                                </div>
                                <div className="mt-1 text-xs text-muted-foreground">{formatPHP(r.total_net)} net</div>
                            </button>
                        ))}
                    </div>
                </Surface>

                <div className="space-y-4">
                    {loading && <Surface className="p-6 text-sm text-muted-foreground">Loading register…</Surface>}

                    {preview && (
                        <>
                            <Surface className="p-5">
                                <div className="flex flex-wrap items-start justify-between gap-3">
                                    <div>
                                        <div className="flex items-center gap-2">
                                            <Landmark className="size-5 text-primary" />
                                            <h3 className="font-display font-semibold">Payout Register</h3>
                                        </div>
                                        <div className="text-xs text-muted-foreground mt-1">{selectedRun?.period_start} → {selectedRun?.period_end} · <span className="capitalize">{String(preview.status).replace(/_/g, " ")}</span></div>
                                    </div>
                                    <div className="text-right">
                                        <div className="text-xs text-muted-foreground">Total to disburse</div>
                                        <div className="font-display text-2xl font-bold tabular-nums">{formatPHP(preview.total_amount)}</div>
                                        <div className="text-xs text-muted-foreground">{preview.payable_count} employee{preview.payable_count === 1 ? "" : "s"}{preview.zero_pay_skipped ? ` · ${preview.zero_pay_skipped} zero-pay skipped` : ""}</div>
                                    </div>
                                </div>

                                {preview.errors?.length > 0 && (
                                    <div className="mt-4 rounded-lg border border-destructive/40 bg-destructive/5 p-3">
                                        <div className="flex items-center gap-2 text-destructive text-sm font-medium"><AlertTriangle className="size-4" /> {preview.errors.length} issue(s) block export</div>
                                        <ul className="mt-1 text-xs text-muted-foreground list-disc pl-5 space-y-0.5">
                                            {preview.errors.slice(0, 8).map((e, i) => <li key={i}>{e}</li>)}
                                            {preview.errors.length > 8 && <li>…and {preview.errors.length - 8} more</li>}
                                        </ul>
                                    </div>
                                )}

                                <TableWrap>
                                    <thead><tr><th>Bank</th><th className="num">Employees</th><th className="num">Amount</th></tr></thead>
                                    <tbody>
                                        {preview.per_bank.map((b) => (
                                            <tr key={b.bank_name}><td className="font-medium">{b.bank_name}</td><td className="num">{b.count}</td><td className="num font-semibold">{formatPHP(b.total)}</td></tr>
                                        ))}
                                        {!preview.per_bank.length && <tr><td colSpan={3}><Empty title="No payable employees" /></td></tr>}
                                    </tbody>
                                </TableWrap>

                                <div className="mt-4 flex flex-wrap items-center gap-2 border-t pt-4">
                                    <label className="text-sm text-muted-foreground">Bank format</label>
                                    <select value={fmt} onChange={(e) => setFmt(e.target.value)} className="h-9 rounded-md border border-border bg-background px-2 text-sm" data-testid="disb-format">
                                        {formats.map((f) => {
                                            const meta = formatItems.find((i) => i.key === f);
                                            return (
                                                <option key={f} value={f}>
                                                    {FMT_LABEL[f] || meta?.name || f}{meta && !meta.verified ? " — layout unconfirmed" : ""}
                                                </option>
                                            );
                                        })}
                                    </select>
                                    <button onClick={download} disabled={!preview.can_export}
                                        className="h-9 px-3 rounded-md bg-primary text-white text-sm font-medium inline-flex items-center gap-2 disabled:opacity-50"
                                        data-testid="disb-download">
                                        <Download className="size-4" /> Download bank file
                                    </button>
                                    {preview.can_export
                                        ? <span className="text-xs text-emerald-600 inline-flex items-center gap-1"><CheckCircle2 className="size-3" /> Ready to disburse</span>
                                        : <span className="text-xs text-muted-foreground">{preview.finalized ? "Resolve the issues above to export." : "Run must be approved/locked first."}</span>}
                                </div>

                                {(() => {
                                    const meta = formatItems.find((i) => i.key === fmt);
                                    if (!meta || meta.verified) return null;
                                    return (
                                        <div className="mt-3 rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900" data-testid="disb-unverified">
                                            <b>Check this file before uploading it.</b> The {meta.name} column layout hasn't been
                                            confirmed against the bank's current payroll template. Compare it with the template your
                                            bank provided — or use the Generic export and map the columns yourself.
                                        </div>
                                    );
                                })()}
                            </Surface>

                            <Surface className="p-5">
                                <h3 className="font-display font-semibold mb-2">Export history</h3>
                                {!history.length && <div className="text-sm text-muted-foreground">No files generated for this run yet.</div>}
                                {history.length > 0 && (
                                    <TableWrap>
                                        <thead><tr><th>Bank</th><th>Version</th><th className="num">Employees</th><th className="num">Amount</th><th>Generated</th><th>Checksum</th></tr></thead>
                                        <tbody>
                                            {history.map((h) => (
                                                <tr key={h.id}>
                                                    <td className="uppercase">{FMT_LABEL[h.bank] || h.bank}</td>
                                                    <td>v{h.version}{h.is_latest && <span className="badge badge-approved ml-1">latest</span>}</td>
                                                    <td className="num">{h.employee_count}</td>
                                                    <td className="num">{formatPHP(h.total_amount)}</td>
                                                    <td className="text-xs text-muted-foreground">{formatDateTime(h.created_at)}</td>
                                                    <td className="font-mono text-[10px] text-muted-foreground">{(h.sha256 || "").slice(0, 12)}…</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </TableWrap>
                                )}
                            </Surface>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
